import { useMemo } from "react";

import { MultiFieldCardItem } from "../../../../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import { DatabaseMetadataPopover } from "../../../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { ContractType } from "../../../../../../graphql/generated.ts";
import { ClikGrantedContractDetails } from "../../../../../../types/clikData/clikContract.ts";
import { toCurrencyPrice } from "../../../../../../utils/number.ts";

interface Props {
  data: ClikGrantedContractDetails | null | undefined;
  type: ContractType;
}

export const useCRCreditLimitDebitBalanceCardsInfo = ({
  data,
  type,
}: Props): MultiFieldCardItem[] => {
  return useMemo(() => {
    if (type === ContractType.Credit) {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "initialCreditLimit",
              }}
              position="right"
            >
              Initial Credit Limit
            </DatabaseMetadataPopover>
          ),
          content:
            data?.initialCreditLimit &&
            toCurrencyPrice(data.initialCreditLimit),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "creditLimit",
              }}
              position="right"
            >
              Credit Limit
            </DatabaseMetadataPopover>
          ),
          content: data?.creditLimit && toCurrencyPrice(data.creditLimit),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "currentMonthRealization",
              }}
              position="right"
            >
              Current Month Realization
            </DatabaseMetadataPopover>
          ),
          content:
            data?.currentMonthRealization &&
            toCurrencyPrice(data.currentMonthRealization),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "debitBalance",
              }}
              position="right"
            >
              Debit Balance
            </DatabaseMetadataPopover>
          ),
          content: data?.debitBalance && toCurrencyPrice(data.debitBalance),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "valueInOriginalCurrency",
              }}
              position="right"
            >
              Value In Currency Origin
            </DatabaseMetadataPopover>
          ),
          content:
            data?.valueInOriginalCurrency &&
            toCurrencyPrice(data.valueInOriginalCurrency),
        },
      ];
    }

    if (type === ContractType.Bond) {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "debitBalance",
              }}
              position="right"
            >
              Debit Balance
            </DatabaseMetadataPopover>
          ),
          content: data?.debitBalance && toCurrencyPrice(data.debitBalance),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "valueInOriginalCurrency",
              }}
              position="right"
            >
              Value In Currency Origin
            </DatabaseMetadataPopover>
          ),
          content:
            data?.valueInOriginalCurrency &&
            toCurrencyPrice(data.valueInOriginalCurrency),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "marketValue",
              }}
              position="right"
            >
              Market Value
            </DatabaseMetadataPopover>
          ),
          content: data?.marketValue && toCurrencyPrice(data.marketValue),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "acquisitionValue",
              }}
              position="right"
            >
              Acquisition Value
            </DatabaseMetadataPopover>
          ),
          content:
            data?.acquisitionValue && toCurrencyPrice(data.acquisitionValue),
        },
      ];
    }

    if (
      type === ContractType.LetterOfCredit ||
      type === ContractType.Guarantee
    ) {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "creditLimit",
              }}
              position="right"
            >
              Credit Limit
            </DatabaseMetadataPopover>
          ),
          content: data?.creditLimit && toCurrencyPrice(data.creditLimit),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "debitBalance",
              }}
              position="right"
            >
              Debit Balance
            </DatabaseMetadataPopover>
          ),
          content: data?.debitBalance && toCurrencyPrice(data.debitBalance),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "depositGuarantee",
              }}
              position="right"
            >
              Deposit Guarantee
            </DatabaseMetadataPopover>
          ),
          content:
            data?.depositGuarantee && toCurrencyPrice(data.depositGuarantee),
        },
      ];
    }

    if (type === ContractType.OtherFacilities) {
      return [
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "debitBalance",
              }}
              position="right"
            >
              Debit Balance
            </DatabaseMetadataPopover>
          ),
          content: data?.debitBalance && toCurrencyPrice(data.debitBalance),
        },
        {
          label: (
            <DatabaseMetadataPopover
              databaseMetadata={{
                tableName: "ClikGrantedContractDetails",
                fieldName: "valueInOriginalCurrency",
              }}
              position="right"
            >
              Value In Currency Origin
            </DatabaseMetadataPopover>
          ),
          content:
            data?.valueInOriginalCurrency &&
            toCurrencyPrice(data.valueInOriginalCurrency),
        },
      ];
    }
    return [];
  }, [data, type]);
};
